import React from "react"; 
import { Box, Typography } from "@mui/material";
import { isStorageFieldVisible } from "./AzureStorageAccountCreation";

const isEmpty = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(", ");

  if (typeof value === "object") {
    if (value.label != null && value.label !== "") return toText(value.label);
    if (value.text != null && value.text !== "") return toText(value.text);
    if ("value" in value) return toText(value.value);

    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }

  return String(value);
};

const toReadableText = (value) =>
  String(value ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

const parseFieldOptions = (field) => {
  if (!field?.dropdownValues) return [];

  try {
    const parsed = JSON.parse(field.dropdownValues);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((option) => {
        if (option && typeof option === "object") {
          const value = option.value ?? option.id ?? option.key ?? "";
          const label = option.label ?? option.text ?? String(value);
          return { value, label };
        }

        const text = String(option ?? "").trim();
        return { value: text, label: text };
      })
      .filter((option) => String(option.value ?? "").trim() !== "");
  } catch {
    return [];
  }
};

const mapValueToFieldLabel = (field, rawValue, dropdownData = {}) => {
  const configuredOptions = parseFieldOptions(field);
  const runtimeOptions = Array.isArray(dropdownData?.[field.fieldName])
    ? dropdownData[field.fieldName]
    : [];

  const options = configuredOptions.length
    ? configuredOptions
    : runtimeOptions.map((option) => ({
        value: option?.value ?? option?.id ?? "",
        label: option?.label ?? option?.text ?? String(option?.value ?? option?.id ?? ""),
      }));

  if (!options.length) {
    if (Array.isArray(rawValue)) {
      return rawValue.map((item) => toReadableText(item)).join(", ");
    }
    return typeof rawValue === "string" ? toReadableText(rawValue) : rawValue;
  }

  const mapSingle = (value) => {
    const found = options.find((option) => String(option.value) === String(value));
    return found?.label ?? toReadableText(value);
  };

  if (Array.isArray(rawValue)) {
    return rawValue.map(mapSingle).join(", ");
  }

  if (typeof rawValue === "string" && rawValue.includes(",")) {
    return rawValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .map(mapSingle)
      .join(", ");
  }

  return mapSingle(rawValue);
};

const splitNames = (value) =>
  String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeDepValue = (value) => String(value ?? "").trim().toLowerCase();

const getEntryValue = (entry) => {
  if (entry && typeof entry === "object" && !Array.isArray(entry) && "value" in entry) {
    return entry.value;
  }
  return entry;
};

const isAzureStorageConfig = (formConfig) =>
  formConfig.some((field) =>
    [field.fieldName, field.fieldLabel].some(
      (name) => normalizeDepValue(name).replace(/[^a-z0-9]/g, "") === "storageaccountname"
    )
  );

const isSummaryFieldVisible = (field, formConfig, formData, isStorageAccount, ancestors = new Set()) => {
  if (field.isDisplay === false) return false;

  const fieldKey = normalizeDepValue(field.fieldName);
  if (ancestors.has(fieldKey)) return false;

  if (
    isStorageAccount &&
    String(field.tabName ?? "").toLowerCase() === "data protection"
  ) {
    return isStorageFieldVisible(field, formConfig, formData);
  }

  const dependencies = splitNames(field.dependentOn);
  if (!dependencies.length) return true;

  const nextAncestors = new Set(ancestors);
  nextAncestors.add(fieldKey);

  const expectedValues = splitNames(field.dependentValue).map(normalizeDepValue);

  return dependencies.every((dependencyName) => {
    const parentField = formConfig.find(
      (candidate) =>
        normalizeDepValue(candidate.fieldName) === normalizeDepValue(dependencyName)
    );

    if (parentField && !isSummaryFieldVisible(parentField, formConfig, formData, isStorageAccount, nextAncestors)) {
      return false;
    }

    const parentEntry = parentField
      ? formData?.[parentField.fieldName]
      : formData?.[dependencyName];

    const fallbackValue = parentField
      ? (parentField.fieldType === "checkbox" ? false : parentField.defaultValue ?? "")
      : "";

    const actualValue = getEntryValue(parentEntry) ?? fallbackValue;
    const normalizedActual = normalizeDepValue(actualValue);

    if (expectedValues.length > 0) {
      return expectedValues.includes(normalizedActual);
    }

    return (
      normalizedActual !== "" &&
      normalizedActual !== "false" &&
      normalizedActual !== "null" &&
      normalizedActual !== "undefined"
    );
  });
};

const getDisplayValue = (field, formData, dropdownData) => {
  const entry = formData?.[field.fieldName];
  const isCheckboxField =
    String(field?.fieldType || "").trim().toLowerCase() === "checkbox";

  if (isCheckboxField) {
    const rawValue =
      entry !== null && entry !== undefined &&
      typeof entry === "object" && !Array.isArray(entry) && "value" in entry
        ? entry.value
        : entry ?? field.defaultValue ?? false;

    const checked =
      rawValue === true ||
      rawValue === 1 ||
      (typeof rawValue === "string" && rawValue.trim().toLowerCase() === "true");

    return checked ? "true" : "false";
  }

  if (entry === null || entry === undefined) return "";

  const rawValue =
    typeof entry === "object" && !Array.isArray(entry) && "value" in entry
      ? entry.value
      : entry;

  const fieldType = String(field?.fieldType || "").trim().toLowerCase();
  const isOptionField = ["dropdown", "multiselectdropdown", "radio button"].includes(fieldType);
  const hasExplicitLabel =
    typeof entry === "object" && !Array.isArray(entry) && !isEmpty(entry.label);

  const displayValue = isOptionField
    ? mapValueToFieldLabel(field, rawValue, dropdownData)
    : hasExplicitLabel
      ? entry.label
      : rawValue;

  if (isEmpty(displayValue)) return "";

  const isSensitive =
    field.fieldType === "password" ||
    /password|secret|token|credential/i.test(
      `${field.fieldName} ${field.fieldLabel || ""}`
    );

  return isSensitive ? "••••••••" : toText(displayValue);
};

export default function AzureSummary({
  selectedProvider,
  formConfig = [],
  formData = {},
  dropdownData = {},
  additionalTags = [],
}) {
  if (selectedProvider?.toUpperCase() !== "AZURE") return null;

  const isStorageAccount = isAzureStorageConfig(formConfig);

  const fieldGroups = new Map();

  formConfig
    .filter(
      (field) =>
        field.tabName &&
        field.tabName.toLowerCase() !== "tag" &&
        field.tabName.toLowerCase() !== "summary" &&
        field.isDisplay !== false &&
        field.fieldType !== "collection"
    )
    .forEach((field) => {
      if (!isSummaryFieldVisible(field, formConfig, formData, isStorageAccount)) return;

      const value = getDisplayValue(field, formData, dropdownData);
      if (!value) return;

      const groupName =
        field.tabName.toLowerCase() === "general" ? "Basics" : field.tabName;

      if (!fieldGroups.has(groupName)) fieldGroups.set(groupName, []);
      fieldGroups.get(groupName).push({
        label: field.fieldLabel || field.fieldName,
        value,
      });
    });

  // Azure tags defined in the form configuration.
  const tagMap = new Map();

  formConfig
    .filter(
      (field) =>
        field.tabName?.toLowerCase() === "tag" &&
        field.isDisplay !== false &&
        field.fieldType !== "collection"
    )
    .forEach((field) => {
      if (!isSummaryFieldVisible(field, formConfig, formData, isStorageAccount)) return;

      const value = getDisplayValue(field, formData, dropdownData);
      if (value) {
        tagMap.set(field.fieldLabel || field.fieldName, value);
      }
    });

  // Azure tags added through the custom Add Tag control.
  additionalTags.forEach((tag) => {
    const key = String(tag?.key || "").trim();
    const value = String(tag?.value || "").trim();

    if (key && value) tagMap.set(key, value);
  });

  const tags = Array.from(tagMap, ([label, value]) => ({ label, value }));

  const renderRows = (rows) =>
    rows.map(({ label, value }, index) => (
      <Box
        key={`${label}-${index}`}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(120px, 1fr)", sm: "minmax(220px, 1fr) 2fr" },
          gap: { xs: 0.5, sm: 2 },
          py: 0.75,
        }}
      >
        <Typography color="text.secondary">{label}</Typography>
        <Typography sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
      </Box>
    ));

  return (
    // <Box sx={{ width: "100%", p: 2 }}>
    <Box
  sx={{
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    p: 2,
    boxSizing: "border-box",
    color: "text.primary",
    scrollbarWidth: "thin",
    scrollbarColor: (theme) => `${theme.palette.text.disabled} transparent`,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "text.disabled",
      borderRadius: "6px",
    },
  }}
>

      {Array.from(fieldGroups, ([groupName, rows]) => (
        <Box key={groupName} sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {groupName}
          </Typography>
          {renderRows(rows)}
        </Box>
      ))}

      {tags.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Tags</Typography>
          {renderRows(tags)}
        </Box>
      )}

      {fieldGroups.size === 0 && tags.length === 0 && (
        <Typography color="text.secondary">
          No Azure values have been entered yet.
        </Typography>
      )}
    </Box>
  );
}
